<template>
  <div id="app"
    :class="[activeContent, {playerVisible : playerShowing}]">
    <template>
      <SiteNavigation />
      <transition name="slide-fade" mode="out-in">
        <!-- key is needed here to trigger transition between elements with the same View -->
        <router-view
          :key="`story-'${activeContent}`"
          :activeName="activeContent"
        >
        </router-view>
      </transition>
    </template>
  </div>
</template>

<script>
import SiteNavigation from './components/SiteNavigation'

export default {
  metaInfo: {
    title: 'Beyond School',
    link: [{
        vmid: 'canonical-url',
        rel: 'canonical',
        href: 'https://globalreportingprogram.org/beyond-school'
    }],
    meta: [
      {property: 'og:site_name', content: 'Beyond School'},
      {property: 'og:type', content: 'website'},
      {name: 'twitter:card', content: 'summary_large_image'},
      {name: 'twitter:site', content: '@GR_Program'},
      {name: 'twitter:creator', content: '@GR_Program'},
      {
        vmid: 'page-description',
        name: 'description',
        content: 'Beyond School explores how new curriculums, alternative teaching methods and reimagined education systems are responding to the individual needs of their students in Norway, Nepal, Kenya and Pakistan.'
      },
      {
        vmid: 'og-title',
        property: 'og:title',
        content: 'Beyond School'
      },
      {
        vmid: 'og-url',
        property: 'og:url',
        content: 'https://globalreportingprogram.org/beyond-school'
      },
      {
        vmid: 'og-image',
        property: 'og:image',
        content: 'https://globalreportingprogram.org' + require(`@/assets/img/social.jpg`)
      },
      {
        vmid: 'og-description',
        property: 'og:description',
        content: 'Beyond School explores how new curriculums, alternative teaching methods and reimagined education systems are responding to the individual needs of their students in Norway, Nepal, Kenya and Pakistan.'
      },
      {
        vmid: 'twitter-title',
        name: 'twitter:title',
        content: 'Beyond School'
      },
      {
        vmid: 'twitter-description',
        name: 'twitter:description',
        content: 'Beyond School explores how new curriculums, alternative teaching methods and reimagined education systems are responding to the individual needs of their students in Norway, Nepal, Kenya and Pakistan.'
      },
      {
        vmid: 'twitter-image',
        name: 'twitter:image',
        content: 'https://globalreportingprogram.org' + require(`@/assets/img/social.jpg`)
      },
      {
        vmid: 'schema-name',
        itemprop: 'name',
        content: 'Beyond School'
      },
      {
        vmid: 'schema-description',
        itemprop: 'description',
        content: 'Beyond School explores how new curriculums, alternative teaching methods and reimagined education systems are responding to the individual needs of their students in Norway, Nepal, Kenya and Pakistan.'
      },
      {
        vmid: 'schema-image',
        itemprop: 'image',
        content: 'https://globalreportingprogram.org' + require(`@/assets/img/social.jpg`)
      }
    ]
  },
  data () {
    return {
      stories: ['pakistan', 'nepal', 'norway', 'kenya'],
      //activeEpisode: 'none',
      activeContent: 'placeholder',
      playerShowing: false,
      isPlaying: false
    }
  },
  components: {
    SiteNavigation
  },
  mounted() {
    this.$nextTick(function () {
      if (this.$route.name !== null) {
        this.randomStory()
      }
    })
  },
  updated() {
    this.$nextTick(function () {
        if (this.$route.name !== 'home') {
          // otherwise we're on a story or transcript page
          let routeName = this.$route.name
          // remove '-transcript' to reuse same country css classes
          if (routeName.endsWith('-transcript')) { this.activeContent = routeName.slice(0, -11) }
          else { this.activeContent = routeName }
        }
        else if (this.activeContent === 'credits') {
          this.randomStory()
        }
    })
  },
  methods: {
    hidePlayer() {
      this.playerShowing = false
    },
    showPlayer() {
      this.playerShowing = true
    },
    playEpisode(episode) {
      this.activeEpisode = episode
      this.isPlaying = true
      this.playerShowing = true
    },
    pauseEpisode() {
      this.isPlaying = false
    },
    randomStory() {
      let randomStorySelect = Math.floor(Math.random() * this.stories.length)
      this.activeContent = this.stories[randomStorySelect]
    }
  }
}

</script>

<style lang="scss">

@import "./styles/_normalize.scss";
@import "./styles/_themes.scss";
@import "./styles/_transitions.scss";

@import url('https://fonts.googleapis.com/css2?family=PT+Serif:ital,wght@0,400;1,400;1,700&family=Playfair+Display:wght@700&display=swap');

// Structure

body {
  font-family: 'PT Serif', serif;
  line-height: 1.6;
}

section {
  padding: 1rem;
}

.container {
  max-width: 34em;
  margin: 0 auto;
}

#app {
  padding-top: 0;
  transition: padding 0.3s;
  &.playerVisible {
    padding-top: 3.6rem;
  }
}

// Type

h1, h2, h3 {
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  line-height: 1.4;
}

h1 {
  margin: 0;
  font-size: 2rem;
  @include md { font-size: 4rem;}
}

p {
  margin: 0 0 1.5rem 0;
  line-height: 1.8;
}

strong {
  font-weight: 700;
}

em {
  font-style: italic;
  font-weight: 700;
}

// Elements

button {
  background: none;
  border: 0;
  &:hover {
    cursor: pointer;
  }

  &.show-player {
    position: fixed;
    z-index: 3;
    top: 0;
    left: 0;
    border-bottom-right-radius: 2px;
    background: none;
    border: none;
    color: $white;
    padding: 1rem;
    background: $black;
    height: 3.6rem;
    @include inset-focus-shadow;
  }
}

p, h2 {
  a {
    transition: color 0.2s;
    &:link, &:visited {
      color: $black;
    }
    &:hover, &:focus {
      color: $black !important;
    }
  }
}

// View-specific Styles

article.story, article.home {
  > header {
    position: relative;
    transition: height 0.3s;

    h1 {
      display: none;
      @include md { position: absolute; display: block; }
      line-height: 1.4;
      span {
        text-transform: none;
        display: block;
        font-size: 0.8em;
      }
    }

    img {
      width: 100%;
      height: auto;
      display: block;
    }
  }

  main .container > h1 {
    margin-bottom: 1rem;
    @include md { display: none; }
  }
}

</style>
